import { BaseColor, getColorCode, theme } from '@prophecy/theme';
import { Badges, Stack, Text } from '@prophecy/ui-v3';
import { ReactNode } from 'react';
import styled from 'styled-components';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReadOnlyRow = {
  label: string;
  value?: string | null;
};

export type ReadOnlySection = {
  /** Section header title. If absent the card has no header row — just bare rows. */
  title?: string;
  badge?: { label: string; variant: 'success' | 'error' };
  rows?: ReadOnlyRow[];
  /** Slot for custom content (e.g. auth card) rendered below the header. */
  content?: ReactNode;
};

export type ReadOnlyFabricViewProps = {
  sections: ReadOnlySection[];
};

type ProviderHeaderProps = {
  providerName: string;
  providerIcon: ReactNode;
  providerColor?: `${BaseColor}`;
};

// ─── Styled components ────────────────────────────────────────────────────────

const ProviderIconBadge = styled.div<{ $color: `${BaseColor}` }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${theme.sizes.x36};
  height: ${theme.sizes.x36};
  border-radius: 50%;
  background: ${({ $color }) => getColorCode($color, 50)};

  svg {
    width: ${theme.sizes.x20};
    height: ${theme.sizes.x20};
  }
`;

const SectionCard = styled.div`
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x8};
  overflow: hidden;
`;

const SectionHeaderRow = styled(Stack)`
  padding: ${theme.spaces.x12} ${theme.spaces.x20};
  border-bottom: 1px solid ${theme.borderColor.secondary};
  background-color: ${theme.colors.gray50};
`;

const DataRow = styled(Stack)`
  padding: ${theme.spaces.x12} ${theme.spaces.x16};
  & + & {
    border-top: 1px solid ${theme.colors.gray100};
  }
`;

const ContentWrapper = styled.div`
  padding: ${theme.spaces.x12} ${theme.spaces.x16};
`;

// ─── Components ───────────────────────────────────────────────────────────────

export const ReadOnlySectionCard = ({ section }: { section: ReadOnlySection }) => {
  const { title, badge, rows, content } = section;
  const hasHeader = Boolean(title);

  return (
    <SectionCard data-test-id='read-only-fabric-section'>
      {hasHeader && (
        <SectionHeaderRow direction='horizontal' align='space-between' alignY='center'>
          <Text level='sm' weight={theme.fontWeight.medium} tone={theme.textColor.tertiary}>
            {title}
          </Text>
          {badge && (
            <Badges tone={badge.variant === 'success' ? 'success' : 'error'} variant='pillLight' size='s'>
              {badge.label}
            </Badges>
          )}
        </SectionHeaderRow>
      )}

      {rows?.map((row) => (
        <DataRow key={row.label} direction='horizontal' align='space-between' alignY='center'>
          <Text level='sm' tone={theme.textColor.tertiary}>
            {row.label}
          </Text>
          <Text level='sm' tone={theme.textColor.secondary}>
            {row.value ?? '—'}
          </Text>
        </DataRow>
      ))}

      {content && <ContentWrapper>{content}</ContentWrapper>}
    </SectionCard>
  );
};

export const ProviderHeader = ({ providerName, providerIcon, providerColor }: ProviderHeaderProps) => (
  <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
    <ProviderIconBadge $color={providerColor ?? BaseColor.grayBlue}>{providerIcon}</ProviderIconBadge>
    <Text level='lg' weight={theme.fontWeight.semiBold} tone={theme.textColor.primary}>
      {providerName}
    </Text>
  </Stack>
);

export const ReadOnlyFabricView = ({ sections }: ReadOnlyFabricViewProps) => {
  return sections.map((section, i) => <ReadOnlySectionCard key={section.title ?? i} section={section} />);
};
